import { useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from 'react'
import { useClickOutside } from '@/lib/useClickOutside'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { enrollInDrip, listDripSequences } from '@/api/drip'
import { AlertTriangle, Check, ChevronDown, Download, ExternalLink, ListPlus, Mail, PanelRight, Plus, Search, Trash2, Upload, User, X } from 'lucide-react'
import {
  upsertContact,
  useBulkAssignOwner,
  useContactIds,
  useContactMatches,
  useContactsPage,
  useContactSources,
  useCreateContact,
  useFieldDefinitions,
} from '@/features/contacts/useContacts'
import { useAuthStore } from '@/features/auth/authStore'
import { useBulkSend } from '@/features/messaging/useMessaging'
import MessageComposer from '@/features/messaging/MessageComposer'
import { useAddToList, useLists } from '@/features/lists/useLists'
import { useMembers } from '@/features/team/useTeam'
import { COLUMNS, PAGE_SIZES, useContactsTableSettings, type ColumnId, type PageSize } from '@/features/contacts/contactsTableSettings'
import SearchableSelect from '@/components/ui/SearchableSelect'
import ContactsTable, { type SortState } from '@/components/ContactsTable'
import ContactsColumnsDrawer from '@/components/ContactsColumnsDrawer'
import type { ContactViewConfig } from '@/api/contactViews'
import ContactDetail from '@/components/ContactDetail'
import PageContainer from '@/components/layout/PageContainer'
import { validateEmail, validatePhone } from '@/lib/contactValidation'
import { exportContactsCsv, type ContactPresetView } from '@/api/contacts'
import { PRESET_VIEWS } from '@/features/contacts/contactPresetViews'
import { AddContactToListModal, AddContactToSequenceModal, DeleteContactModal } from '@/features/contacts/ContactRowModals'
import { useDebouncedValue } from '@/lib/useDebouncedValue'
import { cn } from '@/lib/cn'
import type { Contact } from '@/api/types'
import type { FileRef } from '@/api/files'

const empty = { firstName: '', lastName: '', email: '', mobile: '' }

const IMPORT_LIMIT = 500

type CsvField = 'firstName' | 'lastName' | 'email' | 'phone' | 'mobile' | 'leadSource'

/** Header → contact field, matched after lowercasing and stripping spaces/underscores/dashes. */
const CSV_HEADER_ALIASES: Record<string, CsvField> = {
  firstname: 'firstName',
  first: 'firstName',
  givenname: 'firstName',
  lastname: 'lastName',
  last: 'lastName',
  surname: 'lastName',
  familyname: 'lastName',
  email: 'email',
  emailaddress: 'email',
  mail: 'email',
  phone: 'phone',
  phonenumber: 'phone',
  telephone: 'phone',
  tel: 'phone',
  mobile: 'mobile',
  mobilephone: 'mobile',
  mobilenumber: 'mobile',
  cell: 'mobile',
  cellphone: 'mobile',
  leadsource: 'leadSource',
  source: 'leadSource',
}

/** Minimal CSV parser: quoted fields, escaped quotes (""), commas and newlines inside quotes. */
function parseCsv(text: string): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let field = ''
  let inQuotes = false
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        field += ch
      }
    } else if (ch === '"') {
      inQuotes = true
    } else if (ch === ',') {
      row.push(field)
      field = ''
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++
      row.push(field)
      rows.push(row)
      row = []
      field = ''
    } else {
      field += ch
    }
  }
  if (field !== '' || row.length > 0) {
    row.push(field)
    rows.push(row)
  }
  return rows.filter((r) => r.some((c) => c.trim() !== ''))
}

/** Default order matches the previous view: newest contacts first. */
const DEFAULT_SORT: SortState = { column: 'created', dir: 'desc' }

/** A removable active-filter pill shown under the toolbar. */
function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 bg-brand-primary/10 text-brand-dark rounded-full pl-2.5 pr-1.5 py-1 text-xs">
      {label}
      <button onClick={onRemove} className="text-gray-400 hover:text-danger" aria-label="Remove filter">
        <X size={12} />
      </button>
    </span>
  )
}

const contactName = (c: Contact) => [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || 'Unnamed contact'

/**
 * Existing contacts sharing the e-mail or phone typed into the Add contact form. Adding with a
 * matching e-mail updates that contact instead (the backend upserts by e-mail), so say so up front.
 */
function DuplicateWarning({ email, phone, onOpen }: { email: string; phone: string; onOpen: (id: string) => void }) {
  const { data: matches } = useContactMatches(useDebouncedValue(email, 300), useDebouncedValue(phone, 300))
  if (!matches?.length) return null
  const sameEmail = matches.find((m) => m.email && m.email.toLowerCase() === email.trim().toLowerCase())
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs text-amber-900">
      <p className="flex items-center gap-1.5 font-medium mb-1.5">
        <AlertTriangle size={13} className="shrink-0" />
        {sameEmail
          ? 'This email is already in your contacts — adding will update that contact, not create a new one.'
          : `Already in your contacts with this phone number — check it isn’t the same person.`}
      </p>
      <ul className="space-y-1">
        {matches.map((m) => (
          <li key={m.id} className="flex items-center gap-2">
            <span className="min-w-0 flex-1 truncate">
              <b>{contactName(m)}</b>
              <span className="text-amber-700"> · {[m.email, m.mobile || m.phone].filter(Boolean).join(' · ')}</span>
            </span>
            <button type="button" onClick={() => onOpen(m.id)} className="shrink-0 font-medium text-brand-primary hover:underline">
              Open
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** How many views show as tabs; the rest sit under "More views". */
const MAIN_VIEW_COUNT = 3

type RowModal = { kind: 'list' | 'sequence' | 'delete'; contact: Contact }

export default function Contacts() {
  const create = useCreateContact()
  const bulk = useBulkSend()
  const qc = useQueryClient()
  const [form, setForm] = useState(empty)
  const [showAddForm, setShowAddForm] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [ioMenuOpen, setIoMenuOpen] = useState(false)
  const ioMenuRef = useRef<HTMLDivElement>(null)
  useClickOutside(ioMenuRef, () => setIoMenuOpen(false), ioMenuOpen)
  const [presetView, setPresetView] = useState<ContactPresetView | ''>('')
  const [viewMenuOpen, setViewMenuOpen] = useState(false)
  const viewMenuRef = useRef<HTMLDivElement>(null)
  useClickOutside(viewMenuRef, () => setViewMenuOpen(false), viewMenuOpen)
  const activePreset = PRESET_VIEWS.find((v) => v.value === presetView) ?? PRESET_VIEWS[0]
  const moreViewActive = PRESET_VIEWS.indexOf(activePreset) >= MAIN_VIEW_COUNT
  const [rowModal, setRowModal] = useState<RowModal | null>(null)

  const [manualSelected, setManualSelected] = useState<Set<string>>(new Set())
  // "Select all matching" mode: the selection spans the whole filtered set, not just this page.
  const [allMatching, setAllMatching] = useState(false)
  const [channel, setChannel] = useState('email')
  const [templateId, setTemplateId] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [attachments, setAttachments] = useState<FileRef[]>([])
  const [sentInfo, setSentInfo] = useState<{ sent: number; skipped: number; queued: number } | null>(null)
  const { data: contactFieldDefs } = useFieldDefinitions('contact')
  // Two deep-links open the drawer on load: /contacts/:id opens the contact full-screen
  // (bookmark / "open in new tab") and wins over any click; ?open=<id> (e.g. from a list) opens
  // the collapsed side panel. Deriving the open id (no effect) keeps the route authoritative.
  const { id: routeContactId } = useParams()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  // ?add=1 (the app-wide search's "New contact") opens the Add contact dialog; closing it drops the
  // param too, so it doesn't reopen.
  const addOpen = showAddForm || searchParams.get('add') === '1'
  function closeAdd() {
    setShowAddForm(false)
    if (searchParams.has('add')) {
      setSearchParams((p) => {
        p.delete('add')
        return p
      }, { replace: true })
    }
  }
  const [clickedContactId, setClickedContactId] = useState<string | null>(() => searchParams.get('open'))
  const selectedContactId = routeContactId ?? clickedContactId
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [ownerFilter, setOwnerFilter] = useState('')
  const [sourceFilter, setSourceFilter] = useState('')
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT)
  const [page, setPage] = useState(0)
  const [settings, setSettings] = useContactsTableSettings()
  const [upsertNotice, setUpsertNotice] = useState<string | null>(null)
  const [importProgress, setImportProgress] = useState<{ done: number; total: number } | null>(null)
  const [importSummary, setImportSummary] = useState<string | null>(null)
  const { data: lists } = useLists()
  const { data: members } = useMembers()
  // Dynamic lists compute their members from criteria — only manual ones accept members.
  const manualLists = (lists ?? []).filter((l) => l.listType === 'manual')
  const addToList = useAddToList()
  const [addListId, setAddListId] = useState('')
  const assignOwner = useBulkAssignOwner()
  const [assignOwnerId, setAssignOwnerId] = useState('')
  const { data: dripSequences } = useQuery({ queryKey: ['drip-sequences'], queryFn: listDripSequences })
  const activeSequences = (dripSequences ?? []).filter((s) => s.enabled)
  const enroll = useMutation({
    mutationFn: ({ id, contactIds }: { id: string; contactIds: string[] }) => enrollInDrip(id, { contactIds }),
  })
  const [enrollSeqId, setEnrollSeqId] = useState('')
  const isAdmin = useAuthStore((s) => s.user?.role === 'AGENCY_ADMIN' || s.user?.role === 'PLATFORM_ADMIN')
  // Source options track the selected Owner so you can't pick a source with no matches under it.
  const { data: sources } = useContactSources(ownerFilter ? [ownerFilter] : undefined)

  // Debounce the search box so we don't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(t)
  }, [search])

  // ?list=<id> scopes the whole page to a list's members (the "full view" opened from Lists).
  const listFilter = searchParams.get('list') ?? undefined
  const activeList = listFilter ? (lists ?? []).find((l) => l.id === listFilter) : undefined

  // The shared filter behind the page, its total, and the "select all matching" id list.
  const filters = useMemo(
    () => ({
      q: debouncedSearch || undefined,
      owners: ownerFilter ? [ownerFilter] : undefined,
      source: sourceFilter || undefined,
      list: listFilter,
      view: presetView || undefined,
    }),
    [debouncedSearch, ownerFilter, sourceFilter, listFilter, presetView],
  )

  const sortKey = COLUMNS.find((c) => c.id === sort.column)?.sortKey ?? 'createdAt'
  const { data, isLoading, isFetching } = useContactsPage({
    ...filters,
    sort: sortKey,
    dir: sort.dir,
    page,
    size: settings.pageSize,
  })

  // Lazy — fetched only after the user opts into "select all matching" under the current filter.
  const { data: matchingIds } = useContactIds(filters, allMatching)

  const contacts = useMemo(() => data?.items ?? [], [data])
  const total = data?.total ?? 0
  const memberName = useMemo(() => new Map((members ?? []).map((m) => [m.id, m.fullName])), [members])
  const memberPhoto = useMemo(() => new Map((members ?? []).map((m) => [m.id, m.photoUrl])), [members])

  // Cache every contact we've loaded so the campaign-bar preview can find a selected sample even
  // when it lives on a page we've since paged away from. Grown during render via the guarded
  // setState pattern (same as prevFilters below), keyed on the memoized `contacts` identity.
  const [contactCache, setContactCache] = useState<Map<string, Contact>>(new Map())
  const [cachedFrom, setCachedFrom] = useState(contacts)
  if (cachedFrom !== contacts) {
    setCachedFrom(contacts)
    setContactCache((prev) => {
      const next = new Map(prev)
      for (const c of contacts) next.set(c.id, c)
      return next
    })
  }

  // In "all matching" mode the selection is the server-side id list; otherwise it's the manual set.
  const selected = useMemo(
    () => (allMatching ? new Set(matchingIds ?? []) : manualSelected),
    [allMatching, matchingIds, manualSelected],
  )

  // First selected contact for the composer preview — resolved from the cross-page cache so it
  // survives paging (contacts.find only ever sees the current page).
  const sampleContact = useMemo(() => {
    for (const id of selected) {
      const c = contactCache.get(id)
      if (c) return c
    }
    return undefined
  }, [selected, contactCache])

  // Any filter/search change invalidates a cross-page selection — reset during render. `filters` has
  // a stable identity via useMemo, so this only fires when the filter actually changes.
  const [prevFilters, setPrevFilters] = useState(filters)
  if (prevFilters !== filters) {
    setPrevFilters(filters)
    setManualSelected(new Set())
    setAllMatching(false)
    // Drop the cross-page cache too — it's scoped to the previous filter and would otherwise grow
    // unbounded across a session. Repopulates during render from the current page below.
    setContactCache(new Map())
  }

  // The header checkbox only ever fully selects the current page; when it is fully selected and
  // more rows match the filter than fit on the page, offer the HubSpot-style "select all matching".
  const pageIds = useMemo(() => contacts.map((c) => c.id), [contacts])
  const pageAllSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id))
  const showSelectAllBanner = pageAllSelected && total > contacts.length

  const ownerOptions = useMemo(
    () => [
      { value: '', label: 'Owner: All' },
      ...(members ?? []).map((m) => ({ value: m.id, label: m.fullName })),
    ],
    [members],
  )
  const sourceOptions = useMemo(
    () => [
      { value: '', label: 'Source: All' },
      ...(sources ?? []).map((s) => ({ value: s, label: s })),
    ],
    [sources],
  )

  // Any change to search/filters/sort/page-size drops us back to the first page.
  function resetToFirstPage() {
    setPage(0)
  }

  /** Header click cycles: asc → desc → back to the default (newest first). */
  function cycleSort(column: ColumnId) {
    resetToFirstPage()
    setSort((s) =>
      s.column !== column
        ? { column, dir: 'asc' }
        : s.dir === 'asc'
          ? { column, dir: 'desc' }
          : DEFAULT_SORT,
    )
  }

  function sortDir(column: ColumnId, dir: 'asc' | 'desc') {
    resetToFirstPage()
    setSort({ column, dir })
  }

  const emailError = validateEmail(form.email)
  const phoneError = validatePhone(form.mobile)

  // A contact needs at least a name or an email — otherwise the row is just dashes.
  const canAdd =
    !!(form.firstName.trim() || form.lastName.trim() || form.email.trim()) &&
    !emailError &&
    !phoneError

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!canAdd) return
    create.mutate(
      {
        firstName: form.firstName.trim() || undefined,
        lastName: form.lastName.trim() || undefined,
        email: form.email.trim() || undefined,
        // The number people type here is the client's mobile (what SMS and the table's Mobile column use).
        mobile: form.mobile.trim() || undefined,
      },
      {
        onSuccess: ({ contact, created }) => {
          setForm(empty)
          closeAdd() // the add dialog stayed open over the page after a successful save
          if (created) {
            // Open the new contact in full view so the user can fill in the rest (not the side panel).
            navigate(`/contacts/${contact.id}`)
          } else {
            setClickedContactId(contact.id)
            setUpsertNotice('A contact with this email already exists — we updated that contact instead of creating a duplicate. Find it in the list to review the details.')
          }
        },
      },
    )
  }

  async function onImportFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || importProgress) return
    setImportSummary(null)
    const rows = parseCsv(await file.text())
    if (rows.length < 2) {
      setImportSummary('CSV needs a header row and at least one data row.')
      return
    }
    const fields: (CsvField | null)[] = rows[0].map(
      (h) => CSV_HEADER_ALIASES[h.trim().toLowerCase().replace(/[^a-z0-9]/g, '')] ?? null,
    )
    if (!fields.some(Boolean)) {
      setImportSummary('No recognized columns — expected headers like First Name, Last Name, Email, Phone, Mobile, Lead Source.')
      return
    }
    let skipped = 0
    const params: Partial<Record<CsvField, string>>[] = []
    for (const row of rows.slice(1)) {
      const p: Partial<Record<CsvField, string>> = {}
      fields.forEach((f, i) => {
        const v = row[i]?.trim()
        if (f && v) p[f] = v
      })
      if (!p.firstName && !p.lastName && !p.email) {
        skipped++
        continue
      }
      params.push(p)
    }
    const truncated = params.length > IMPORT_LIMIT
    const toImport = params.slice(0, IMPORT_LIMIT)
    let created = 0
    let updated = 0
    for (let i = 0; i < toImport.length; i++) {
      setImportProgress({ done: i, total: toImport.length })
      try {
        const res = await upsertContact(toImport[i])
        if (res.created) created++
        else updated++
      } catch {
        skipped++
      }
    }
    setImportProgress(null)
    setImportSummary(
      `Imported: ${created} new, ${updated} updated, ${skipped} skipped (no name/email or error).` +
        (truncated ? ` Only the first ${IMPORT_LIMIT} rows were processed — split larger files.` : ''),
    )
    qc.invalidateQueries({ queryKey: ['contacts'] })
  }

  function toggle(id: string) {
    // Seed from the effective selection so unchecking a row in "all matching" mode keeps the rest.
    const next = new Set(allMatching ? (matchingIds ?? []) : manualSelected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setManualSelected(next)
    setAllMatching(false) // a single row change breaks the "all matching" invariant
  }
  function toggleAll() {
    const base = new Set(allMatching ? (matchingIds ?? []) : manualSelected)
    const ids = contacts.map((c) => c.id)
    const allSelected = ids.length > 0 && ids.every((id) => base.has(id))
    if (allSelected) ids.forEach((id) => base.delete(id))
    else ids.forEach((id) => base.add(id))
    setManualSelected(base)
    setAllMatching(false) // the header checkbox is page-scoped
  }

  /** Opt into selecting every contact under the current filter (ids fetched lazily, then applied). */
  function selectAllMatching() {
    setAllMatching(true)
  }
  function clearSelection() {
    setAllMatching(false)
    setManualSelected(new Set())
  }

  function sendCampaign({ includeSignature }: { includeSignature: boolean }) {
    if (selected.size === 0 || (!templateId && !body.trim())) return
    bulk.mutate(
      {
        contactIds: [...selected],
        channel,
        templateId: templateId || undefined,
        subject: templateId || channel !== 'email' ? undefined : subject,
        body: templateId ? undefined : body,
        attachments: channel === 'email' && attachments.length
          ? attachments.map((f) => ({ key: f.key, filename: f.filename, contentType: f.contentType }))
          : undefined,
        includeSignature,
      },
      {
        onSuccess: (res) => {
          setSentInfo(res)
          clearSelection()
          setBody('')
          setSubject('')
          setTemplateId('')
          setAttachments([])
        },
      },
    )
  }

  // Current table state as a saveable view, and applying a saved one back onto the page.
  const currentViewConfig: ContactViewConfig = {
    q: debouncedSearch || undefined,
    owner: ownerFilter || undefined,
    source: sourceFilter || undefined,
    view: presetView || undefined,
    sortColumn: sort.column,
    sortDir: sort.dir,
    columns: settings.columns,
    pageSize: settings.pageSize,
  }
  async function onExport() {
    setExporting(true)
    try {
      const blob = await exportContactsCsv(filters)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'contacts.csv'
      a.click()
      URL.revokeObjectURL(url)
    } finally {
      setExporting(false)
    }
  }

  function applyView(cfg: ContactViewConfig) {
    setSearch(cfg.q ?? '')
    setDebouncedSearch(cfg.q ?? '')
    setOwnerFilter(cfg.owner ?? '')
    setSourceFilter(cfg.source ?? '')
    setPresetView(PRESET_VIEWS.find((v) => v.value === cfg.view)?.value ?? '')
    if (cfg.sortColumn) setSort({ column: cfg.sortColumn, dir: cfg.sortDir ?? 'asc' })
    const pageSize = (PAGE_SIZES as readonly number[]).includes(cfg.pageSize ?? -1)
      ? (cfg.pageSize as PageSize)
      : settings.pageSize
    setSettings({
      ...settings,
      columns: cfg.columns && cfg.columns.length ? ['name', ...cfg.columns.filter((c) => c !== 'name')] : settings.columns,
      pageSize,
    })
    setPage(0)
  }

  return (
    <PageContainer width="full">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h1 className="text-xl font-semibold text-brand-darkest">
          Contacts
          {!isLoading && <span className="text-gray-400 font-normal tabular-nums"> · {total.toLocaleString('en-AU')}</span>}
        </h1>
        <div className="flex items-center gap-2">
          <div className="relative" ref={ioMenuRef}>
            <button
              type="button"
              onClick={() => setIoMenuOpen((v) => !v)}
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50"
            >
              <Upload size={14} /> Import / Export
              <ChevronDown size={14} className="text-gray-400" />
            </button>
            {ioMenuOpen && (
              <div className="absolute right-0 z-30 mt-1 w-44 bg-white border border-gray-200 rounded-xl shadow-lg p-1">
                <label
                  className={`flex items-center gap-2 px-3 py-1.5 text-sm text-brand-dark hover:bg-gray-50 rounded-lg cursor-pointer ${
                    importProgress ? 'opacity-60 pointer-events-none' : ''
                  }`}
                >
                  <Upload size={13} /> Import CSV
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    className="hidden"
                    onChange={(e) => {
                      onImportFile(e)
                      setIoMenuOpen(false)
                    }}
                  />
                </label>
                <button
                  type="button"
                  onClick={() => {
                    onExport()
                    setIoMenuOpen(false)
                  }}
                  disabled={exporting || total === 0}
                  className="flex items-center gap-2 w-full text-left px-3 py-1.5 text-sm text-brand-dark hover:bg-gray-50 rounded-lg disabled:opacity-50"
                >
                  <Download size={13} /> {exporting ? 'Exporting…' : 'Export CSV'}
                </button>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className="px-4 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark flex items-center gap-1"
          >
            <Plus size={15} /> Add contact
          </button>
        </div>
      </div>

      {/* Views as tabs, as in HubSpot: the everyday ones always visible, the rest one click away. */}
      <div className="flex flex-wrap items-center gap-x-1 border-b border-gray-200 mb-4" role="tablist" aria-label="Views">
        {PRESET_VIEWS.slice(0, MAIN_VIEW_COUNT).map((v) => (
          <button
            key={v.value}
            type="button"
            role="tab"
            aria-selected={v.value === presetView}
            title={v.hint}
            onClick={() => { setPresetView(v.value); setPage(0) }}
            className={cn(
              'px-3 py-2 text-sm whitespace-nowrap -mb-px border-b-2',
              v.value === presetView ? 'border-brand-primary text-brand-primary font-medium' : 'border-transparent text-gray-500 hover:text-brand-dark',
            )}
          >
            {v.label}
          </button>
        ))}
        <div className="relative" ref={viewMenuRef}>
          <button
            type="button"
            onClick={() => setViewMenuOpen((v) => !v)}
            aria-haspopup="listbox"
            aria-expanded={viewMenuOpen}
            className={cn(
              'flex items-center gap-1 px-3 py-2 text-sm whitespace-nowrap -mb-px border-b-2',
              moreViewActive ? 'border-brand-primary text-brand-primary font-medium' : 'border-transparent text-gray-500 hover:text-brand-dark',
            )}
          >
            {moreViewActive ? activePreset.label : 'More views'} <ChevronDown size={14} />
          </button>
          {viewMenuOpen && (
            <div role="listbox" aria-label="More views" className="absolute right-0 sm:left-0 sm:right-auto z-30 mt-1 w-72 bg-white border border-gray-200 rounded-xl shadow-lg p-1">
              {PRESET_VIEWS.slice(MAIN_VIEW_COUNT).map((v) => (
                <button
                  key={v.value}
                  type="button"
                  role="option"
                  aria-selected={v.value === presetView}
                  onClick={() => {
                    setPresetView(v.value)
                    setPage(0)
                    setViewMenuOpen(false)
                  }}
                  className="flex items-start gap-2 w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50"
                >
                  <span className="w-4 pt-0.5 shrink-0 text-brand-primary">{v.value === presetView && <Check size={14} />}</span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-brand-darkest">{v.label}</span>
                    <span className="block text-xs text-gray-500">{v.hint}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {listFilter && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-brand-primary/20 bg-brand-primary/5 px-3 py-2 text-sm">
          <span className="text-gray-600">
            Viewing list <b className="text-brand-dark">{activeList?.name ?? '…'}</b>
            {activeList && <span className="text-gray-400"> · {activeList.listType === 'dynamic' ? 'dynamic' : 'manual'}</span>}
          </span>
          <button
            type="button"
            onClick={() => navigate('/contacts')}
            className="ml-auto text-xs text-brand-primary hover:text-brand-dark"
          >
            Back to all contacts
          </button>
        </div>
      )}

      {addOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={closeAdd} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-brand-darkest">Add contact</h2>
              <button
                type="button"
                onClick={closeAdd}
                className="text-gray-400 hover:text-gray-600"
                title="Close"
              >
                <X size={18} />
              </button>
            </div>
            <form onSubmit={onSubmit} className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {([['firstName', 'First name'], ['lastName', 'Last name']] as const).map(([k, label]) => (
                  <div key={k}>
                    <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
                    <input
                      value={form[k]}
                      aria-label={label}
                      onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
                    />
                  </div>
                ))}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
                <input
                  value={form.email}
                  aria-label="Email"
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40 ${
                    emailError ? 'border-danger' : 'border-gray-200'
                  }`}
                />
                {emailError && <p className="text-danger text-[11px] mt-0.5">{emailError}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Mobile</label>
                <input
                  value={form.mobile}
                  aria-label="Mobile"
                  inputMode="tel"
                  placeholder="e.g. 0412 345 678"
                  onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40 ${
                    phoneError ? 'border-danger' : 'border-gray-200'
                  }`}
                />
                {phoneError && <p className="text-danger text-[11px] mt-0.5">{phoneError}</p>}
              </div>
              <DuplicateWarning
                email={form.email}
                phone={form.mobile}
                onOpen={(id) => {
                  setShowAddForm(false)
                  navigate(`/contacts/${id}`)
                }}
              />
              <p className="text-[11px] text-gray-400">
                Enter at least a name or an email. You'll open the new contact to fill in the rest.
              </p>
              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={closeAdd}
                  className="px-3 py-2 text-sm text-gray-500 hover:text-brand-dark"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={create.isPending || !canAdd}
                  title={canAdd ? undefined : 'Enter at least a name or an email (and valid email/phone)'}
                  className="px-4 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {create.isPending ? 'Adding…' : 'Add contact'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {importProgress && (
        <p className="text-xs text-gray-500 mb-3">
          Importing {importProgress.done + 1}/{importProgress.total}…
        </p>
      )}
      {importSummary && !importProgress && (
        <div className="flex items-start gap-2 bg-white border border-gray-200 text-gray-600 rounded-lg px-3 py-2 mb-3 text-xs">
          <span className="flex-1">{importSummary}</span>
          <button onClick={() => setImportSummary(null)} className="text-gray-400 hover:text-gray-600 shrink-0" aria-label="Dismiss">
            <X size={14} />
          </button>
        </div>
      )}

      {upsertNotice && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-300 text-amber-800 rounded-xl px-4 py-3 mb-4 text-sm">
          <span className="flex-1">{upsertNotice}</span>
          <button onClick={() => setUpsertNotice(null)} className="text-amber-500 hover:text-amber-700 shrink-0" aria-label="Dismiss">
            <X size={16} />
          </button>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(0)
            }}
            placeholder="Search by name, email or phone…"
            className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
          />
        </div>
        <SearchableSelect
          size="md"
          value={ownerFilter}
          onChange={(v) => {
            setOwnerFilter(v)
            setSourceFilter('') // the previous source may not exist under the new owner
            setPage(0)
          }}
          placeholder="Owner: All"
          options={ownerOptions}
          className="w-44"
        />
        <SearchableSelect
          size="md"
          value={sourceFilter}
          onChange={(v) => {
            setSourceFilter(v)
            setPage(0)
          }}
          placeholder="Source: All"
          options={sourceOptions}
          className="w-44"
        />
        <ContactsColumnsDrawer
          settings={settings}
          onSettingsChange={setSettings}
          fieldDefs={contactFieldDefs ?? []}
          currentView={currentViewConfig}
          onApplyView={applyView}
        />
      </div>

      {(search || ownerFilter || sourceFilter) && (
        <div className="flex flex-wrap items-center gap-1.5 mb-4">
          {search && (
            <FilterChip label={`Search: “${search}”`} onRemove={() => { setSearch(''); setDebouncedSearch(''); setPage(0) }} />
          )}
          {ownerFilter && (
            <FilterChip
              label={`Owner: ${memberName.get(ownerFilter) ?? '—'}`}
              onRemove={() => { setOwnerFilter(''); setSourceFilter(''); setPage(0) }}
            />
          )}
          {sourceFilter && (
            <FilterChip label={`Source: ${sourceFilter}`} onRemove={() => { setSourceFilter(''); setPage(0) }} />
          )}
          <button
            onClick={() => { setSearch(''); setDebouncedSearch(''); setOwnerFilter(''); setSourceFilter(''); setPage(0) }}
            className="text-xs text-gray-500 hover:text-brand-dark underline"
          >
            Clear all
          </button>
        </div>
      )}

      {/* campaign bar */}
      {selected.size > 0 && (
        <div className="bg-brand-darkest text-white rounded-xl p-3 mb-4 flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium mr-1">{selected.size} selected</span>
          <MessageComposer
            channel={channel}
            onChannelChange={setChannel}
            templateId={templateId}
            onTemplateChange={setTemplateId}
            subject={subject}
            onSubjectChange={setSubject}
            body={body}
            onBodyChange={setBody}
            recipientCount={selected.size}
            onSend={sendCampaign}
            sending={bulk.isPending}
            sampleContact={sampleContact}
            // each recipient gets their own render; preview it for the first one
            previewTarget={sampleContact ? { kind: 'contact', id: sampleContact.id } : undefined}
            tone="onDark"
            allowAttachments
            attachments={attachments}
            onAttachmentsChange={setAttachments}
          />
          {manualLists.length > 0 && (
            <>
              <select value={addListId} onChange={(e) => setAddListId(e.target.value)} className="px-2 py-1.5 rounded-lg text-xs bg-white border border-white/40 text-brand-darkest focus:outline-none focus:ring-2 focus:ring-white/60">
                <option value="">add to list…</option>
                {manualLists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
              <button
                onClick={() => {
                  if (addListId) {
                    addToList.mutate(
                      { listId: addListId, contactIds: [...selected] },
                      { onSuccess: () => { clearSelection(); setAddListId('') } },
                    )
                  }
                }}
                disabled={!addListId || addToList.isPending}
                className="px-3 py-1.5 bg-white/20 text-white rounded-lg text-sm hover:bg-white/30 disabled:opacity-50"
              >
                Add
              </button>
            </>
          )}
          {isAdmin && (
            <>
              <select
                value={assignOwnerId}
                onChange={(e) => setAssignOwnerId(e.target.value)}
                aria-label="Assign owner"
                className="px-2 py-1.5 rounded-lg text-xs bg-white border border-white/40 text-brand-darkest focus:outline-none focus:ring-2 focus:ring-white/60"
              >
                <option value="">assign owner…</option>
                <option value="__unassign__">Unassigned</option>
                {(members ?? []).map((m) => (
                  <option key={m.id} value={m.id}>{m.fullName}</option>
                ))}
              </select>
              <button
                onClick={() => {
                  if (!assignOwnerId) return
                  assignOwner.mutate(
                    { contactIds: [...selected], ownerUserId: assignOwnerId === '__unassign__' ? null : assignOwnerId },
                    { onSuccess: () => { clearSelection(); setAssignOwnerId('') } },
                  )
                }}
                disabled={!assignOwnerId || assignOwner.isPending}
                className="px-3 py-1.5 bg-white/20 text-white rounded-lg text-sm hover:bg-white/30 disabled:opacity-50"
              >
                {assignOwner.isPending ? 'Assigning…' : 'Assign'}
              </button>
            </>
          )}
          {activeSequences.length > 0 && (
            <>
              <select
                value={enrollSeqId}
                onChange={(e) => setEnrollSeqId(e.target.value)}
                aria-label="Add to sequence"
                className="px-2 py-1.5 rounded-lg text-xs bg-white border border-white/40 text-brand-darkest focus:outline-none focus:ring-2 focus:ring-white/60"
              >
                <option value="">add to sequence…</option>
                {activeSequences.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              <button
                onClick={() => {
                  if (!enrollSeqId) return
                  enroll.mutate(
                    { id: enrollSeqId, contactIds: [...selected] },
                    { onSuccess: () => { clearSelection(); setEnrollSeqId('') } },
                  )
                }}
                disabled={!enrollSeqId || enroll.isPending}
                className="px-3 py-1.5 bg-white/20 text-white rounded-lg text-sm hover:bg-white/30 disabled:opacity-50"
              >
                {enroll.isPending ? 'Enrolling…' : 'Enrol'}
              </button>
            </>
          )}
        </div>
      )}

      {sentInfo !== null && selected.size === 0 && (
        <p className="text-xs text-success mb-3">
          {sentInfo.queued > 0
            ? `Queued ${sentInfo.queued} message${sentInfo.queued === 1 ? '' : 's'} — sending in the background.`
            : `Sent ${sentInfo.sent}${sentInfo.skipped > 0 ? `, skipped ${sentInfo.skipped} (opted out)` : ''}.`}
        </p>
      )}

      {showSelectAllBanner && (
        <div className="flex flex-wrap items-center justify-center gap-1.5 bg-brand-primary/5 border border-brand-primary/20 text-brand-dark rounded-lg px-4 py-2 mb-3 text-sm">
          {allMatching ? (
            <>
              <span>All {total.toLocaleString('en-AU')} contacts matching your filters are selected.</span>
              <button onClick={clearSelection} className="font-medium underline hover:text-brand-primary">
                Clear selection
              </button>
            </>
          ) : (
            <>
              <span>All {contacts.length} on this page are selected.</span>
              <button onClick={selectAllMatching} className="font-medium underline hover:text-brand-primary">
                Select all {total.toLocaleString('en-AU')} matching
              </button>
            </>
          )}
        </div>
      )}

      <ContactsTable
        contacts={contacts}
        total={total}
        page={page}
        isLoading={isLoading || isFetching}
        sort={sort}
        onCycleSort={cycleSort}
        onSortDir={sortDir}
        settings={settings}
        onSettingsChange={setSettings}
        onPageChange={setPage}
        selected={selected}
        onToggle={toggle}
        onToggleAll={toggleAll}
        memberName={memberName}
        memberPhoto={memberPhoto}
        onOpen={(id) => navigate(`/contacts/${id}`)}
        onPreview={setClickedContactId}
        fieldDefs={contactFieldDefs ?? []}
        rowActions={(c) => [
          { label: 'Open', icon: <User size={15} />, onClick: () => navigate(`/contacts/${c.id}`) },
          { label: 'Preview in side panel', icon: <PanelRight size={15} />, onClick: () => setClickedContactId(c.id) },
          { label: 'Open in new tab', icon: <ExternalLink size={15} />, onClick: () => window.open(`/contacts/${c.id}`, '_blank', 'noopener') },
          { label: 'Add to list…', icon: <ListPlus size={15} />, onClick: () => setRowModal({ kind: 'list', contact: c }) },
          { label: 'Add to sequence…', icon: <Mail size={15} />, onClick: () => setRowModal({ kind: 'sequence', contact: c }) },
          { label: 'Delete contact', icon: <Trash2 size={15} />, danger: true, onClick: () => setRowModal({ kind: 'delete', contact: c }) },
        ]}
      />

      {rowModal?.kind === 'list' && <AddContactToListModal contact={rowModal.contact} onClose={() => setRowModal(null)} />}
      {rowModal?.kind === 'sequence' && <AddContactToSequenceModal contact={rowModal.contact} onClose={() => setRowModal(null)} />}
      {rowModal?.kind === 'delete' && <DeleteContactModal contact={rowModal.contact} onClose={() => setRowModal(null)} />}

      {selectedContactId && (
        <ContactDetail
          contactId={selectedContactId}
          defaultExpanded={!!routeContactId}
          onClose={() => {
            // Arrived via /contacts/:id — drop the id from the URL on close; otherwise it was a
            // click- or ?open=-driven side panel, so just clear local state.
            if (routeContactId) navigate('/contacts', { replace: true })
            else setClickedContactId(null)
          }}
        />
      )}
    </PageContainer>
  )
}
